import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/useSystem'
import useSystems from '@hooks/useSystems'
import type { SystemsFilters } from '@hooks/useSystems'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import SystemListItem from '../SystemListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
  ]
}

interface PageContentProps {
  pageFilters: SystemsFilters,
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  const systemsPayload = useSystems({
    filters: pageFilters,
    performHttpRequests: true,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredSystems,
    filteredSystemsCount,
    hasFilteredSystems,
    loading,
  } = systemsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateSystemModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { systemsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Systems | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateSystemModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="systems"
        tabBarItems={[
          {
            href: systemsIndexUrl,
            icon: faCheckCircle,
            key: 'systems',
            title: 'Systems',
          },
        ]}
        title="Systems"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredSystems && <ListBlankState />}

            {!loading && hasFilteredSystems && (
              <DashboardModule title="Systems">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredSystems.map(system => (
                      <SystemListItem key={system.id} system={system} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredSystemsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
